import { useQuery } from "@apollo/client";
import { useEffect, useRef } from "react";
import {
  AssessmentState,
  GetComplianceProgramActionsQuery,
  GetComplianceProgramActionsQueryVariables,
} from "types/graphql-schema";

import useCreateEmptyAssessment from "@apps/assessments/hooks/use-create-empty-assessment";
import getComplianceProgramActionsQuery from "@apps/programs/graphql/get-compliance-program-actions-query";
import useLabel from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";
import Button from "@components/button/button";
import Layout from "@components/layout/layout";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import { assertEdgesNonNull } from "@helpers/helpers";
import usePkParams from "@helpers/hooks/use-pk-params";

const StartReview = () => {
  const label = useLabel();
  const { replace } = useLink();
  const attempted = useRef("");
  const complianceProgramId = usePkParams("complianceProgramId");
  const templateId = usePkParams("templateId");
  const targetId = usePkParams("targetId");
  const user = currentUserVar();
  const actionsUrl = `/assessments/program/${complianceProgramId}/actions`;
  const { handleCreateEmptyAssessment, isLoading, error } =
    useCreateEmptyAssessment(actionsUrl);
  const {
    data,
    loading,
    error: queryError,
    refetch,
  } = useQuery<
    GetComplianceProgramActionsQuery,
    GetComplianceProgramActionsQueryVariables
  >(getComplianceProgramActionsQuery, {
    variables: {
      complianceProgramId,
      responderId: user.id,
      deliveryTargetIds: [],
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  const program = data?.complianceProgram;
  const existing = assertEdgesNonNull(
    program?.assessments ?? { edges: [] },
  ).find(
    (assessment) =>
      assessment.state === AssessmentState.Draft &&
      assessment.template.id === templateId &&
      assessment.target?.id === targetId,
  );
  const assignment = program?.usersMissingAssessment?.edges.find(
    (edge) =>
      edge?.node?.responderId === user.id &&
      edge.node.assessmentTemplateId === templateId &&
      edge.node.target?.id === targetId,
  )?.node;
  const existingId = existing?.id;
  const hasAssignment = Boolean(assignment?.target);
  const requestKey = `${user.id}/${complianceProgramId}/${templateId}/${targetId}`;
  useEffect(() => {
    if (loading || queryError || attempted.current === requestKey) return;
    if (existingId) {
      attempted.current = requestKey;
      replace(`/assessments/assessment/${existingId}`);
    } else if (hasAssignment) {
      attempted.current = requestKey;
      handleCreateEmptyAssessment(complianceProgramId, templateId, targetId);
    }
  }, [
    loading,
    queryError,
    requestKey,
    existingId,
    hasAssignment,
    replace,
    handleCreateEmptyAssessment,
    complianceProgramId,
    templateId,
    targetId,
  ]);

  return (
    <Layout>
      <Layout.Header
        breadcrumbs={[
          {
            title: label("review", { capitalize: true, pluralize: true }),
            url: "/assessments",
          },
        ]}
      />
      <Layout.Container>
        <Layout.Main width="default">
          <Layout.MainSection
            title={program?.title || label("review", { capitalize: true })}
          >
            {queryError && !loading ? (
              <div className="flex flex-col items-start gap-4">
                <p>Unable to load your {label("review")}. Please try again.</p>
                <Button
                  text="Try again"
                  onClick={() => void refetch().catch(() => {})}
                />
              </div>
            ) : loading ||
              isLoading ||
              existingId ||
              (hasAssignment && !error) ? (
              <Loading>{`Opening your ${label("review")}...`}</Loading>
            ) : hasAssignment ? (
              <div className="flex flex-col items-start gap-4">
                <p>Unable to open your {label("review")}. Please try again.</p>
                <Button
                  text="Try again"
                  onClick={() =>
                    handleCreateEmptyAssessment(
                      complianceProgramId,
                      templateId,
                      targetId,
                    )
                  }
                />
              </div>
            ) : (
              <p>
                This {label("review")} is not available for your account. It may
                have changed since the reminder was sent.
              </p>
            )}
          </Layout.MainSection>
        </Layout.Main>
      </Layout.Container>
    </Layout>
  );
};

export default StartReview;
